import axios from 'axios';
import logger from './utils/logger';

// ✅ STRICT: Force local backend only - NO direct API calls
const BACKEND_URL = process.env.REACT_APP_BACKEND;
const CHART_API_URL = process.env.REACT_APP_BACKEND_Chart;

logger.log('🔧 API Configuration:', {
  backendUrl: BACKEND_URL,
  isProduction: process.env.NODE_ENV === 'production'
});

const api = axios.create({
  baseURL: BACKEND_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  timeout: 300000
});

api.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('astroguru_token');

    // Add token to Authorization header if it exists
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      logger.log('🔐 Token added to request:', config.url);
    }

    logger.log('📤 API REQUEST:', {
      method: config.method?.toUpperCase(),
      url: `${config.baseURL}${config.url}`,
      hasData: !!config.data,
      hasToken: !!token,
      timestamp: new Date().toISOString()
    });

    return config;
  },
  (error) => {
    logger.error(' REQUEST SETUP ERROR:', error);
    return Promise.reject(error);
  }
);

// ═══════════════════════════════════════════════════════════════════════════════
// ✅ RESPONSE INTERCEPTOR - HANDLE TOKEN EXPIRATION
// ═══════════════════════════════════════════════════════════════════════════════

api.interceptors.response.use(
  (response) => {
    logger.log('✅ BACKEND RESPONSE SUCCESS:', {
      status: response.status,
      url: response.config.url,
      hasData: !!response.data,
      timestamp: new Date().toISOString()
    });
    return response;
  },
  (error) => {
    logger.error(' BACKEND RESPONSE ERROR:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      message: error.message,
      timestamp: new Date().toISOString()
    });

    // Handle 401 Unauthorized - Token expired or invalid
    if (error.response?.status === 401) {
      logger.error('🔒 UNAUTHORIZED - Token expired or invalid');

      // Clear token and user data
      localStorage.removeItem('astroguru_token');
      localStorage.removeItem('astroguru_user');

      // Redirect to login page
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

// ✅ STRICT: searchPlaces MUST go through backend - NO EXCEPTIONS
export const searchPlaces = async (query, country = 'IN') => {
  try {
    logger.log('🔍 SEARCHING PLACES THROUGH BACKEND ONLY:', {
      query,
      country,
      backendUrl: BACKEND_URL,
      note: 'NO DIRECT API CALLS ALLOWED'
    });

    // ✅ FORCE CHECK: Backend must be available
    const response = await api.post('/astro/places/search', {
      query: query.trim(),
      country: country
    });

    // ✅ STRICT VERIFICATION: Must come from local backend
    if (!response.data.source || !response.data.source.includes('LOCAL_API')) {
      logger.error('🚨 CRITICAL: Response did not come from local backend!');
      logger.error('🚨 Response source:', response.data.source);
      logger.error('🚨 This means your backend is not configured correctly!');
      throw new Error('Backend verification failed - response not from local API');
    }

    if (!response.data.provider || !response.data.provider.includes('Backend')) {
      logger.warn('⚠️ Provider verification failed:', response.data.provider);
    }

    logger.log('✅ VERIFIED BACKEND RESPONSE:', {
      success: response.data.success,
      count: response.data.places?.length || 0,
      provider: response.data.provider,
      source: response.data.source,
      debug: response.data.debug
    });

    return response;
  } catch (error) {
    logger.error(' BACKEND SEARCH ERROR:', error);

    // ✅ NO FALLBACK - Force failure when backend is down
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      logger.error('🚨 BACKEND IS DOWN - NO DIRECT API FALLBACK ALLOWED');
      throw new Error(' Backend server is not running! Please start your local API server on port 4000.');
    }

    if (error.message?.includes('Backend verification failed')) {
      throw new Error(' Backend response verification failed - check your backend configuration.');
    }

    throw error;
  }
};

// ✅ All other functions remain the same
export const getPlaceDetails = async (placeId) => {
  try {
    const response = await api.get(`/astro/places/details/${placeId}`);
    return response;
  } catch (error) {
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running!');
    }
    throw error;
  }
};

export const sendMessage = (text) => api.post('/astro/message', { message: text });
export const googleSignup = (googleToken, profile = {}) => api.post('/astro/signup', { token: googleToken, profile });
export const googleLogin = (googleToken) => {
  logger.log('Google Token:', googleToken);
  return api.post('/astro/login', { token: googleToken });
};


// ✅ NEW: User Settings API Functions
export const getUserSettings = async (userId) => {
  try {
    logger.log('⚙️ FETCHING USER SETTINGS THROUGH BACKEND:', { userId });

    const response = await api.get(`/astro/settings/user/${userId}`);

    logger.log('✅ USER SETTINGS LOADED:', {
      success: true,
      settingId: response.data.SettingId,
      language: response.data.Language,
      messageType: response.data.MessageType
    });

    return response;
  } catch (error) {
    logger.error(' GET USER SETTINGS ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

export const updateUserSettings = async (settingId, updates) => {
  try {
    logger.log('⚙️ UPDATING USER SETTINGS THROUGH BACKEND:', {
      settingId,
      updates
    });

    const response = await api.put(`/astro/settings/${settingId}`, updates);

    logger.log('✅ USER SETTINGS UPDATED:', {
      success: true,
      message: response.data.message,
      updatedFields: response.data.updatedFields
    });

    return response;
  } catch (error) {
    logger.error(' UPDATE USER SETTINGS ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

/*
// Get preferred language
export const updateUserPreferences = (userId, preferences) =>
  api.post(`/astro/user/preferences/${userId}`, preferences);

export const getUserPreferences = (userId) =>
  api.get(`/astro/user/preferences/${userId}`);
*/


// Update user profile
export const updateUserProfile = async (userId, profileData) => {
  try {
    logger.log('📤 Updating user profile:', userId, profileData);

    const response = await api.put(`/astro/profile/${userId}`, profileData);

    logger.log('✅ Profile updated:', response.data);
    return response;
  } catch (error) {
    logger.error(' Profile update error:', error);
    throw error;
  }
};

// Get user profile
export const getUserProfile = async (userId) => {
  try {
    logger.log('📥 Fetching user profile:', userId);

    const response = await api.get(`/astro/profile/${userId}`);

    logger.log('✅ Profile fetched:', response.data);
    return response;
  } catch (error) {
    logger.error(' Profile fetch error:', error);
    throw error;
  }
};


//export const getUserProfile = (userId) => api.get(`/astro/profile/${userId}`);
export const deductCredits = (userId, amount, reason = 'Question asked') =>
  api.post(`/astro/credits/${userId}/deduct`, { amount, reason });
export const addCredits = (userId, amount, reason = 'Credits purchased', packageInfo = null) =>
  api.post(`/astro/credits/${userId}/add`, { amount, reason, packageInfo });
export const getCreditHistory = (userId) => api.get(`/astro/credits/${userId}/history`);


// Get user credits (unchanged)
export const getUserCredits = (userId) => api.get(`/astro/credits/${userId}`);

export const saveChatMessage = async (userId, userMessage, profile, settings, aiContextMessage = null, queryProfile = null, d1ChartData = null) => {
  try {
    logger.log('💬 SAVING CHAT MESSAGE:', {
      userId,
      userMessageLength: userMessage.length,
      hasAIContext: !!aiContextMessage,
      hasQueryProfile: !!queryProfile,
      hasIsTempProfile: profile?.isTempProfile,
      queryProfileName: queryProfile?.name || profile?.name,
      profile,
      settings
    });

    // ✅ Detect if using temp profile
    const isTempProfile = profile?.isTempProfile === true;

    // ✅ Get user's real profile from localStorage
    const userProfile = JSON.parse(localStorage.getItem('astroguru_user') || '{}');

    // ✅ Construct payload with combined user + temp profile
    const payload = {
      userId,
      userMessage,
      aiContextMessage,
      settings,
      d1ChartData: d1ChartData || null,
      // ✅ Always send user profile
      userProfile: {
        userId: userProfile.userId || userProfile.id,
        email: userProfile.email,
        full_name: userProfile.full_name,
        religion: userProfile.religion,
        birth_date: userProfile.birth_date,
        birth_time: userProfile.birth_time,
        birth_place: userProfile.birth_place,
        country_code: userProfile.country_code
      },
      // ✅ Send analysis profile (temp or user)
      profile: isTempProfile ? {
        ...profile,
        isTempProfile: true
      } : profile,
      // ✅ Legacy support for queryProfile
      queryProfile: queryProfile || null
    };

    logger.log('📤 API Request Payload:', payload);

    const response = await api.post('/astro/chat/save-message', payload, {
      timeout: 30000
    });

    return response.data;
  } catch (error) {
    logger.error(' SAVE CHAT MESSAGE ERROR:', error);
    throw error;
  }
};



// Get quick AI response WITHOUT saving to database
export const getQuickResponse = async (userMessage, profile, settings, d1ChartData = null) => {
  try {
    logger.log('⚡ GETTING QUICK RESPONSE:', {
      userMessageLength: userMessage.length,
      profile,
      settings
    });

    const response = await api.post('/astro/chat/quickResponse', {
      userMessage,
      profile,
      settings,
      d1ChartData: d1ChartData || null
    }, {
      timeout: 30000
    });

    return response.data;
  } catch (error) {
    logger.error(' QUICK RESPONSE ERROR:', error);
    throw error;
  }
};


export const getChatHistory = async (userId) => {
  try {
    logger.log('📜 FETCHING CHAT HISTORY THROUGH BACKEND:', { userId });

    const response = await api.get(`/astro/chat/history/${userId}`);

    logger.log('✅ CHAT HISTORY LOADED:', {
      success: response.data.success,
      totalMessages: response.data.totalMessages,
      dateCount: Object.keys(response.data.data || {}).length
    });

    return response;
  } catch (error) {
    logger.error(' GET CHAT HISTORY ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

export const deleteChatHistory = async (userId) => {
  try {
    logger.log('🗑️ DELETING CHAT HISTORY THROUGH BACKEND:', { userId });

    const response = await api.delete(`/astro/chat/history/${userId}`);

    logger.log('✅ CHAT HISTORY DELETED:', {
      success: response.data.success,
      deletedCount: response.data.deletedCount,
      message: response.data.message
    });

    return response;
  } catch (error) {
    logger.error(' DELETE CHAT HISTORY ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};


// ✅ NEW: Backend prompt API functions
export const getAllPrompts = async () => {
  return api.get('/astro/prompt');
};

export const getPrompt = async (path) => {
  return api.get(`/astro/prompt/get/${path}`);
};

export const getTypingMessages = async (religion = 'HINDU') => {
  return api.get(`/astro/prompt/typing/${religion}`);
};

export const getReligionData = async (religion = 'HINDU') => {
  return api.get(`/astro/prompt/religion/${religion}`);
};

export const getPromptsVersion = async () => {
  return api.get('/astro/prompt/version');
};

export const reloadPrompts = async () => {
  return api.post('/astro/prompt/reload');
};


// ✅ NEW: CALL GURU API FUNCTIONS - Following your exact pattern
export const getAllGurus = async () => {
  try {
    logger.log('🧙‍♂️ FETCHING ALL GURUS THROUGH BACKEND');
    const response = await api.get('/astro/call-guru/gurus');
    logger.log('✅ GURUS LOADED:', {
      success: response.data.success,
      count: response.data.count,
      message: response.data.message
    });
    return response;
  } catch (error) {
    logger.error(' GET GURUS ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

export const getAllConsultationTypes = async () => {
  try {
    logger.log('🔮 FETCHING CONSULTATION TYPES THROUGH BACKEND');
    const response = await api.get('/astro/call-guru/consultation-types');
    logger.log('✅ CONSULTATION TYPES LOADED:', {
      success: response.data.success,
      count: response.data.count,
      message: response.data.message
    });
    return response;
  } catch (error) {
    logger.error(' GET CONSULTATION TYPES ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

export const getGuruAvailability = async (guruId, startDate = null, endDate = null) => {
  try {
    logger.log('📅 FETCHING GURU AVAILABILITY THROUGH BACKEND:', {
      guruId,
      startDate,
      endDate
    });

    let url = `/astro/call-guru/availability/${guruId}`;
    const params = new URLSearchParams();

    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await api.get(url);
    logger.log('✅ GURU AVAILABILITY LOADED:', {
      success: response.data.success,
      count: response.data.count,
      guruId: response.data.guruId,
      dateRange: response.data.dateRange
    });
    return response;
  } catch (error) {
    logger.error(' GET GURU AVAILABILITY ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

export const getWeekAvailability = async (startDate = null) => {
  try {
    logger.log('📅 FETCHING WEEK AVAILABILITY THROUGH BACKEND:', { startDate });

    let url = '/astro/call-guru/availability/week';
    if (startDate) {
      url += `?startDate=${startDate}`;
    }

    const response = await api.get(url);
    logger.log('✅ WEEK AVAILABILITY LOADED:', {
      success: response.data.success,
      count: response.data.count,
      dateRange: response.data.dateRange
    });
    return response;
  } catch (error) {
    logger.error(' GET WEEK AVAILABILITY ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

export const createBooking = async (bookingData) => {
  try {
    logger.log('📞 CREATING BOOKING THROUGH BACKEND:', {
      userId: bookingData.userId,
      guruId: bookingData.guruId,
      consultationTypeId: bookingData.consultationTypeId,
      bookingDate: bookingData.bookingDate,
      startHour: bookingData.startHour,
      startMinute: bookingData.startMinute
    });

    const response = await api.post('/astro/call-guru/bookings', bookingData);
    logger.log('✅ BOOKING CREATED SUCCESSFULLY:', {
      success: response.data.success,
      bookingId: response.data.booking?.id,
      message: response.data.message
    });
    return response;
  } catch (error) {
    logger.error(' CREATE BOOKING ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

export const getUserBookings = async (userId, status = null, limit = 50) => {
  try {
    logger.log('📋 FETCHING USER BOOKINGS THROUGH BACKEND:', {
      userId,
      status,
      limit
    });

    let url = `/astro/call-guru/bookings/user/${userId}`;
    const params = new URLSearchParams();

    if (status) params.append('status', status);
    if (limit) params.append('limit', limit.toString());

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await api.get(url);
    logger.log('✅ USER BOOKINGS LOADED:', {
      success: response.data.success,
      count: response.data.count,
      userId: response.data.userId,
      filters: response.data.filters
    });
    return response;
  } catch (error) {
    logger.error(' GET USER BOOKINGS ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

export const getBookingDetails = async (bookingId, userId = null) => {
  try {
    logger.log('📋 FETCHING BOOKING DETAILS THROUGH BACKEND:', {
      bookingId,
      userId
    });

    let url = `/astro/call-guru/bookings/${bookingId}`;
    if (userId) {
      url += `?userId=${userId}`;
    }

    const response = await api.get(url);
    logger.log('✅ BOOKING DETAILS LOADED:', {
      success: response.data.success,
      bookingId: response.data.data?.id,
      message: response.data.message
    });
    return response;
  } catch (error) {
    logger.error(' GET BOOKING DETAILS ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

export const cancelBooking = async (bookingId, userId, reason = 'Cancelled by user') => {
  try {
    logger.log(' CANCELLING BOOKING THROUGH BACKEND:', {
      bookingId,
      userId,
      reason
    });

    const response = await api.patch(`/astro/call-guru/bookings/${bookingId}/cancel`, {
      userId,
      reason
    });
    logger.log('✅ BOOKING CANCELLED SUCCESSFULLY:', {
      success: response.data.success,
      bookingId: response.data.booking?.id,
      message: response.data.message
    });
    return response;
  } catch (error) {
    logger.error(' CANCEL BOOKING ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};


export const healthCheck = async () => {
  try {
    const response = await api.get('/health', { timeout: 5000 });
    return response;
  } catch (error) {
    throw error;
  }
};

const getClientIP = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json', { timeout: 3000 });
    const data = await response.json();
    return data.ip || 'unknown';
  } catch (error) {
    logger.warn('⚠️ Could not fetch IP address:', error);
    return 'unknown';
  }
};

// ✅ NEW: SESSION MANAGEMENT API FUNCTIONS
// ═══════════════════════════════════════════════════════

/**
 * Create a new session when user logs in
 * @param {number} userId - User ID from astroguru_users.id
 * @param {string} ipAddress - Client IP address (optional)
 * @param {string} deviceInfo - Browser/device info (optional)
 */
export const createSession = async (userId, ipAddress = null, deviceInfo = null) => {
  try {
    logger.log('🔐 CREATING SESSION THROUGH BACKEND:', {
      userId,
      ipAddress: ipAddress || 'auto-detect',
      deviceInfo: deviceInfo || 'browser-default'
    });

    const response = await api.post('/astro/session/login', {
      userId,
      ipAddress: ipAddress || (await getClientIP()),
      deviceInfo: deviceInfo || navigator.userAgent
    });

    logger.log('✅ SESSION CREATED:', {
      success: response.data.success,
      sessionId: response.data.session?.sessionId,
      userName: response.data.session?.userName,
      message: response.data.message
    });

    return response;
  } catch (error) {
    logger.error(' CREATE SESSION ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

/**
 * End session when user logs out
 * @param {number} sessionId - Specific session ID (optional)
 * @param {number} userId - User ID (optional, required if no sessionId)
 */
export const endSession = async (sessionId = null, userId = null) => {
  try {
    logger.log('🚪 ENDING SESSION THROUGH BACKEND:', {
      sessionId: sessionId || 'all',
      userId
    });

    const response = await api.post('/astro/session/logout', {
      sessionId,
      userId
    });

    logger.log('✅ SESSION ENDED:', {
      success: response.data.success,
      sessionsEnded: response.data.sessionsEnded || 1,
      message: response.data.message
    });

    return response;
  } catch (error) {
    logger.error(' END SESSION ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

/**
 * Get all sessions for a user (history)
 * @param {number} userId - User ID from astroguru_users.id
 */
export const getUserSessions = async (userId) => {
  try {
    logger.log('📊 FETCHING USER SESSIONS THROUGH BACKEND:', { userId });

    const response = await api.get(`/astro/session/history/${userId}`);

    logger.log('✅ USER SESSIONS LOADED:', {
      success: response.data.success,
      totalSessions: response.data.summary?.totalSessions,
      activeSessions: response.data.summary?.activeSessions,
      closedSessions: response.data.summary?.closedSessions
    });

    return response;
  } catch (error) {
    logger.error(' GET USER SESSIONS ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

/**
 * Check if user has an active session
 * @param {number} userId - User ID from astroguru_users.id
 */
export const getActiveSession = async (userId) => {
  try {
    logger.log('🔍 CHECKING ACTIVE SESSION THROUGH BACKEND:', { userId });

    const response = await api.get(`/astro/session/active/${userId}`);

    logger.log('✅ ACTIVE SESSION CHECK:', {
      success: response.data.success,
      hasActiveSession: response.data.hasActiveSession,
      sessionId: response.data.session?.sessionId
    });

    return response;
  } catch (error) {
    logger.error(' GET ACTIVE SESSION ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

/**
 * Helper function to get client IP address
 * @returns {Promise<string>} IP address or 'unknown'
 */


// ✅ NEW: REFERRAL CODE API FUNCTIONS
// ═══════════════════════════════════════════════════════

/**
 * Get user's referral code
 * @param {number} userId - User ID from astroguru_users.id
 */
export const getUserReferralCode = async (userId) => {
  try {
    logger.log('🎫 FETCHING REFERRAL CODE THROUGH BACKEND:', { userId });

    const response = await api.get(`/astro/referral/${userId}`);

    logger.log('✅ REFERRAL CODE LOADED:', {
      success: response.data.success,
      referralCode: response.data.referralCode,
      hasCode: response.data.hasReferralCode
    });

    return response;
  } catch (error) {
    logger.error(' GET REFERRAL CODE ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

/**
 * Generate referral code for user
 * @param {number} userId - User ID
 * @param {boolean} forceRegenerate - Force regenerate if already exists
 */
export const generateUserReferralCode = async (userId, forceRegenerate = false) => {
  try {
    logger.log('🎫 GENERATING REFERRAL CODE THROUGH BACKEND:', {
      userId,
      forceRegenerate
    });

    const response = await api.post('/astro/referral/generate', {
      userId,
      forceRegenerate
    });

    logger.log('✅ REFERRAL CODE GENERATED:', {
      success: response.data.success,
      referralCode: response.data.referralCode,
      isNew: response.data.isNew
    });

    return response;
  } catch (error) {
    logger.error(' GENERATE REFERRAL CODE ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

/**
 * Verify if referral code is valid
 * @param {string} code - Referral code to verify
 */
export const verifyReferralCode = async (code) => {
  try {
    logger.log('🔍 VERIFYING REFERRAL CODE THROUGH BACKEND:', { code });

    const response = await api.get(`/astro/referral/verify/${code}`);

    logger.log('✅ REFERRAL CODE VERIFIED:', {
      success: response.data.success,
      isValid: response.data.isValid,
      referredBy: response.data.referredBy
    });

    return response;
  } catch (error) {
    logger.error(' VERIFY REFERRAL CODE ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};


export const getAllCountries = async () => {
  try {
    logger.log('🌍 FETCHING ALL COUNTRIES THROUGH BACKEND');
    const response = await api.get('/astro/countries');
    logger.log('✅ COUNTRIES LOADED:', {
      success: response.data.success,
      count: response.data.count,
      version: response.data.version
    });
    return response;
  } catch (error) {
    logger.error(' GET ALL COUNTRIES ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

/**
 * Get popular countries (top 10)
 */
export const getPopularCountries = async () => {
  try {
    logger.log('🌍 FETCHING POPULAR COUNTRIES THROUGH BACKEND');
    const response = await api.get('/astro/countries/popular');
    logger.log('✅ POPULAR COUNTRIES LOADED:', {
      success: response.data.success,
      count: response.data.count
    });
    return response;
  } catch (error) {
    logger.error(' GET POPULAR COUNTRIES ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

/**
 * Get countries by region
 * @param {string} region - Region name (e.g., 'South Asia', 'Middle East')
 */
export const getCountriesByRegion = async (region) => {
  try {
    logger.log('🌍 FETCHING COUNTRIES BY REGION THROUGH BACKEND:', { region });
    const response = await api.get(`/astro/countries/region/${region}`);
    logger.log('✅ COUNTRIES BY REGION LOADED:', {
      success: response.data.success,
      region: response.data.region,
      count: response.data.count
    });
    return response;
  } catch (error) {
    logger.error(' GET COUNTRIES BY REGION ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

/**
 * Get all regions
 */
export const getAllRegions = async () => {
  try {
    logger.log('🌍 FETCHING ALL REGIONS THROUGH BACKEND');
    const response = await api.get('/astro/countries/regions');
    logger.log('✅ REGIONS LOADED:', {
      success: response.data.success,
      count: response.data.count
    });
    return response;
  } catch (error) {
    logger.error(' GET ALL REGIONS ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

/**
 * Search countries by name, ISO code, or phone code
 * @param {string} query - Search query
 */
export const searchCountries = async (query) => {
  try {
    logger.log('🔍 SEARCHING COUNTRIES THROUGH BACKEND:', { query });
    const response = await api.get(`/astro/countries/search/${query}`);
    logger.log('✅ COUNTRIES SEARCH RESULTS:', {
      success: response.data.success,
      query: response.data.query,
      count: response.data.count
    });
    return response;
  } catch (error) {
    logger.error(' SEARCH COUNTRIES ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};


// ✅ NEW: REFERRAL MILESTONE API FUNCTIONS
// ═══════════════════════════════════════════════════════

/**
 * Get all referral milestones configuration
 * @param {number} userId - Optional: User ID to include their progress
 */
export const getReferralMilestones = async (userId = null) => {
  try {
    logger.log('🎯 FETCHING REFERRAL MILESTONES THROUGH BACKEND:', { userId });

    let url = '/astro/referral/milestones';
    if (userId) {
      url += `?userId=${userId}`;
    }

    const response = await api.get(url);

    logger.log('✅ REFERRAL MILESTONES LOADED:', {
      success: response.data.success,
      totalMilestones: response.data.milestones?.length,
      hasUserProgress: !!response.data.userProgress,
      currentReferrals: response.data.currentReferrals
    });

    return response;
  } catch (error) {
    logger.error(' GET REFERRAL MILESTONES ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

/**
 * Get user's referral progress and milestone status
 * @param {number} userId - User ID from astroguru_users.id
 */
export const getUserReferralProgress = async (userId) => {
  try {
    logger.log('📊 FETCHING USER REFERRAL PROGRESS THROUGH BACKEND:', { userId });

    const response = await api.get(`/astro/referral/progress/${userId}`);

    logger.log('✅ USER REFERRAL PROGRESS LOADED:', {
      success: response.data.success,
      referralCount: response.data.referralCount,
      completedMilestones: response.data.completedMilestones?.length,
      totalCreditsEarned: response.data.totalCreditsEarned,
      nextMilestone: response.data.progress?.nextMilestone?.name
    });

    return response;
  } catch (error) {
    logger.error(' GET USER REFERRAL PROGRESS ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

/**
 * Get referral history for a user
 * @param {number} userId - User ID from astroguru_users.id
 */
export const getUserReferralHistory = async (userId) => {
  try {
    logger.log('📜 FETCHING USER REFERRAL HISTORY THROUGH BACKEND:', { userId });

    const response = await api.get(`/astro/referral/history/${userId}`);

    logger.log('✅ USER REFERRAL HISTORY LOADED:', {
      success: response.data.success,
      totalReferrals: response.data.totalReferrals,
      completedCount: response.data.completedCount,
      pendingCount: response.data.pendingCount
    });

    return response;
  } catch (error) {
    logger.error(' GET USER REFERRAL HISTORY ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

/**
 * Get milestone rewards history for a user
 * @param {number} userId - User ID from astroguru_users.id
 */
export const getUserMilestoneRewards = async (userId) => {
  try {
    logger.log('🏆 FETCHING USER MILESTONE REWARDS THROUGH BACKEND:', { userId });

    const response = await api.get(`/astro/referral/rewards/${userId}`);

    logger.log('✅ USER MILESTONE REWARDS LOADED:', {
      success: response.data.success,
      totalRewards: response.data.totalRewards,
      totalCredits: response.data.totalCredits,
      rewardsCount: response.data.rewards?.length
    });

    return response;
  } catch (error) {
    logger.error(' GET USER MILESTONE REWARDS ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

/**
 * Get referral leaderboard (top referrers)
 * @param {number} limit - Number of top referrers to fetch (default: 10)
 */
export const getReferralLeaderboard = async (limit = 10) => {
  try {
    logger.log('🏆 FETCHING REFERRAL LEADERBOARD THROUGH BACKEND:', { limit });

    const response = await api.get(`/astro/referral/leaderboard?limit=${limit}`);

    logger.log('✅ REFERRAL LEADERBOARD LOADED:', {
      success: response.data.success,
      topReferrersCount: response.data.topReferrers?.length,
      totalActive: response.data.totalActive
    });

    return response;
  } catch (error) {
    logger.error(' GET REFERRAL LEADERBOARD ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

/**
 * Get all available credit packages
 * @returns {Promise} Array of credit packages
 */
export const getCreditPackages = () => {
  logger.log('📦 Fetching credit packages from API...');
  return api.get('/astro/credit-packages');
};

/**
 * Get specific credit package by ID
 * @param {number} packageId - Package ID
 * @returns {Promise} Package details
 */
export const getCreditPackage = (packageId) => {
  logger.log(`📦 Fetching credit package ${packageId}...`);
  return api.get(`/astro/credit-packages/${packageId}`);
};

/**
 * Get package summary by ID
 * @param {number} packageId - Package ID
 * @returns {Promise} Package summary
 */
export const getCreditPackageSummary = (packageId) => {
  logger.log(`📊 Fetching package summary ${packageId}...`);
  return api.get(`/astro/credit-packages/${packageId}/summary`);
};

/**
 * Purchase credits
 * @param {number} userId - User ID
 * @param {number} packageId - Package ID
 * @param {string} transactionId - Transaction ID
 * @param {string} paymentGateway - Payment gateway (default: 'razorpay')
 * @param {string} paymentMethod - Payment method (default: 'online')
 * @returns {Promise} Purchase result
 */
export const purchaseCredits = (userId, packageId, transactionId, purchaseType = 'credit_pack', purchaseSource = 'web', paymentGateway = 'razorpay', paymentMethod = 'online', currency = 'INR', paymentStatus = 'completed') => {
  logger.log('💳 Processing credit purchase...', { paymentStatus });
  return api.post('/astro/credits/purchase', {
    userId,
    packageId,
    transactionId,
    purchaseType,
    purchaseSource,
    paymentGateway,
    paymentMethod,
    currency,
    paymentStatus
  });
};

/**
 * Verify and complete payment
 * @param {string} txnid - Transaction ID
 */
export const verifyAndCompletePayment = async (txnid) => {
  try {
    logger.log('🔍 Verifying and completing payment:', txnid);
    const response = await api.post(`/astro/payment/verify-complete/${txnid}`);
    logger.log('✅ Payment verification response:', response.data);
    return response;
  } catch (error) {
    logger.error('❌ VERIFY AND COMPLETE ERROR:', error);
    throw error;
  }
};

// Payment History API
export const getUserPaymentHistory = (userId, page = 1, limit = 10) => 
  api.get(`/astro/paymenthistory/${userId}`, {
    params: { page: parseInt(page) || 1, limit: parseInt(limit) || 10 }
  });

// ✅ ADD THIS NEW FUNCTION
export const getPaymentDetails = (txnid) => {
  logger.log('💳 Fetching payment details for txnid:', txnid);
  return api.get(`/astro/payment/details/${txnid}`);
};

export const getSpendTypes = () => {
  logger.log('💸 Fetching spend types...');
  return api.get('/astro/spend-types');
};


export const spendCredits = (userId, spendTypeId, description = null, promoCode = null) => {
  logger.log('💸 Spending credits...', { userId, spendTypeId, description });
  return api.post('/astro/credits/spend', {
    userId,
    spendTypeId,
    description,
    promoCode
  });
};


export const sendEmailNotification = async (template, recipientEmail, data) => {
  try {
    logger.log('📧 SENDING EMAIL NOTIFICATION THROUGH BACKEND:', {
      template,
      recipient: recipientEmail,
      hasData: !!data
    });

    const response = await api.post('/astro/notifications/send', {
      template,
      recipientEmail,
      data
    });

    logger.log('✅ EMAIL NOTIFICATION SENT:', {
      success: response.data.success,
      messageId: response.data.messageId,
      template: response.data.template,
      recipient: response.data.recipient
    });

    return response;
  } catch (error) {
    logger.error(' SEND EMAIL NOTIFICATION ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};


export const submitFeedback = async (formData) => {
  try {
    logger.log('💬 SUBMITTING FEEDBACK THROUGH BACKEND');

    const response = await api.post('/astro/feedback/submit', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 60000 // 60 seconds for file uploads
    });

    logger.log('✅ FEEDBACK SUBMITTED:', {
      success: response.data.success,
      feedbackId: response.data.feedbackId,
      attachmentsCount: response.data.attachmentsCount
    });

    return response;
  } catch (error) {
    logger.error(' SUBMIT FEEDBACK ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

/**
 * Get all feedback (Admin)
 * @param {string} status - Filter by status (pending, reviewed, resolved)
 * @param {string} type - Filter by type (feedback, issue)
 * @param {number} limit - Number of records to fetch
 * @param {number} offset - Offset for pagination
 * @returns {Promise} Feedback list
 */
export const getAllFeedback = async (status = null, type = null, limit = 50, offset = 0) => {
  try {
    logger.log('📜 FETCHING ALL FEEDBACK THROUGH BACKEND:', {
      status,
      type,
      limit,
      offset
    });

    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (type) params.append('type', type);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());

    const response = await api.get(`/astro/feedback/all?${params.toString()}`);

    logger.log('✅ ALL FEEDBACK LOADED:', {
      success: response.data.success,
      count: response.data.count
    });

    return response;
  } catch (error) {
    logger.error(' GET ALL FEEDBACK ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};


export const getUserFeedback = async (userId) => {
  try {
    logger.log('📜 FETCHING USER FEEDBACK THROUGH BACKEND:', { userId });

    const response = await api.get(`/astro/feedback/user/${userId}`);

    logger.log('✅ USER FEEDBACK LOADED:', {
      success: response.data.success,
      count: response.data.count
    });

    return response;
  } catch (error) {
    logger.error(' GET USER FEEDBACK ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

export const updateFeedbackStatus = async (feedbackId, status, modifiedBy) => {
  try {
    logger.log('🔄 UPDATING FEEDBACK STATUS THROUGH BACKEND:', {
      feedbackId,
      status,
      modifiedBy
    });

    const response = await api.put(`/astro/feedback/status/${feedbackId}`, {
      status,
      modifiedBy
    });

    logger.log('✅ FEEDBACK STATUS UPDATED:', {
      success: response.data.success,
      message: response.data.message
    });

    return response;
  } catch (error) {
    logger.error(' UPDATE FEEDBACK STATUS ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

export const deleteFeedback = async (feedbackId, modifiedBy) => {
  try {
    logger.log('🗑️ DELETING FEEDBACK THROUGH BACKEND:', {
      feedbackId,
      modifiedBy
    });

    const response = await api.delete(`/astro/feedback/${feedbackId}`, {
      data: { modifiedBy }
    });

    logger.log('✅ FEEDBACK DELETED:', {
      success: response.data.success,
      message: response.data.message
    });

    return response;
  } catch (error) {
    logger.error(' DELETE FEEDBACK ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

export const checkUserFeedback = async (userId) => {
  try {
    const response = await api.get(`/astro/feedback/check`, {
      params: { userId }
    });
    return response.data;
  } catch (error) {
    logger.error(' Check feedback error:', error);
    throw error;
  }
};

export const getReportTypes = async () => {
  try {
    logger.log('📊 FETCHING REPORT TYPES THROUGH BACKEND');
    const response = await api.get('/astro/reports/types');
    logger.log('✅ REPORT TYPES LOADED:', {
      success: response.data.success,
      count: response.data.types?.length
    });
    return response;
  } catch (error) {
    logger.error(' GET REPORT TYPES ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error('Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

export const generateReport = async (reportData) => {
  try {
    logger.log('📝 GENERATING REPORT THROUGH BACKEND:', {
      reportType: reportData.reportType,
      userId: reportData.userId
    });

    const response = await api.post('/astro/reports/generate', reportData);

    logger.log('✅ REPORT GENERATED:', {
      success: response.data.success,
      cached: response.data.cached,
      reportLength: response.data.reportContent?.length
    });

    return response;
  } catch (error) {
    logger.error(' GENERATE REPORT ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error('Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};


export const getReportHistory = async (userId) => {
  try {
    logger.log('📜 FETCHING REPORT HISTORY THROUGH BACKEND:', userId);
    const response = await api.get(`/astro/reports/history/${userId}`);
    logger.log('✅ REPORT HISTORY LOADED:', {
      success: response.data.success,
      count: response.data.reports?.length
    });
    return response;
  } catch (error) {
    logger.error(' GET REPORT HISTORY ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error('Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

export const getLoggerStatus = async () => {
  try {
    const response = await api.get('/config/logger-status');
    return response.data;
  } catch (error) {
    logger.warn('⚠️ Could not fetch logger status from backend:', error.message);
    return { success: true, loggingEnabled: true }; // Default to enabled on error
  }
};

export const generateD1Chart = async (chartData) => {
  try {
    logger.log('📊 GENERATING D1 CHART (RASHI):', {
      chartApiUrl: CHART_API_URL,
      hasData: !!chartData,
      name: chartData.name,
      dob: chartData.dateOfBirth,
      location: chartData.placeOfBirth
    });

    // ✅ Validate Chart API URL
    if (!CHART_API_URL) {
      throw new Error(' REACT_APP_BACKEND_Chart is not configured in .env file!');
    }

    // ✅ Call external charts API
    const response = await axios.post(
      `${CHART_API_URL}/api/v1/d1-chart`,
      chartData,
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        timeout: 30000 // 30 seconds timeout
      }
    );

    logger.log('✅ D1 CHART GENERATED SUCCESSFULLY:', {
      success: response.data.success,
      chartType: 'D1 Rashi',
      hasPlanets: !!response.data.planets,
      hasHouses: !!response.data.houses,
      timestamp: response.data.timestamp
    });

    return response;
  } catch (error) {
    logger.error(' GENERATE D1 CHART ERROR:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: `${CHART_API_URL}/api/v1/d1-chart`
    });

    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Charts API server is not reachable! Please check if the service is running.');
    }

    if (error.response?.status === 400) {
      throw new Error(` Invalid chart data: ${error.response?.data?.message || 'Please check your input'}`);
    }

    throw error;
  }
};

export const getD1ChartHealth = async () => {
  try {
    logger.log('🏥 CHECKING D1 CHART API HEALTH:', { url: CHART_API_URL });

    if (!CHART_API_URL) {
      throw new Error(' REACT_APP_BACKEND_Chart is not configured!');
    }

    const response = await axios.get(`${CHART_API_URL}/health`, {
      timeout: 5000
    });

    logger.log('✅ D1 CHART API HEALTHY:', response.data);
    return response;
  } catch (error) {
    logger.error(' D1 CHART API HEALTH CHECK FAILED:', error);
    throw error;
  }
};


export const getOrCreateD1Chart = async (chartData) => {
  try {
    logger.log('📊 GET OR CREATE D1 CHART THROUGH BACKEND:', {
      userId: chartData.user_id,
      datetime: chartData.datetime,
      place: chartData.place
    });

    const response = await api.post('/astro/d1chart', chartData);

    logger.log('✅ D1 CHART LOADED:', {
      success: response.data.success,
      source: response.data.source, // 'database' or 'api'
      chartId: response.data.data?.d1ChartsId,
      cached: response.data.source === 'database'
    });

    return response;
  } catch (error) {
    logger.error(' GET OR CREATE D1 CHART ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

export const getUserD1Charts = async (userId) => {
  try {
    logger.log('📊 FETCHING USER D1 CHARTS THROUGH BACKEND:', { userId });
    
    const response = await api.get(`/astro/d1charts/user/${userId}`);
    
    logger.log('✅ USER D1 CHARTS LOADED:', {
      success: response.data.success,
      count: response.data.count
    });
    
    return response;
  } catch (error) {
    logger.error(' GET USER D1 CHARTS ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

export const getD1ChartById = async (chartId) => {
  try {
    logger.log('📊 FETCHING D1 CHART BY ID THROUGH BACKEND:', { chartId });
    
    const response = await api.get(`/astro/d1chart/${chartId}`);
    
    logger.log('✅ D1 CHART BY ID LOADED:', {
      success: response.data.success,
      chartId: response.data.chart?.chartId
    });
    
    return response;
  } catch (error) {
    logger.error(' GET D1 CHART BY ID ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};


export const deleteD1Chart = async (chartId, userId) => {
  try {
    logger.log('🗑️ DELETING D1 CHART THROUGH BACKEND:', { chartId, userId });
    
    const response = await api.delete(`/astro/d1chart/${chartId}`, {
      data: { userId }
    });
    
    logger.log('✅ D1 CHART DELETED:', {
      success: response.data.success,
      chartId: response.data.chartId
    });
    
    return response;
  } catch (error) {
    logger.error(' DELETE D1 CHART ERROR:', error);
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error(' Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};


// api.js - ADD THIS FUNCTION

/**
 * Get or create D9 (Navamsa) chart
 * @param {Object} chartData - Chart generation data
 * @returns {Promise} D9 chart data
 */
export const getOrCreateD9Chart = async (chartData) => {
  try {
    logger.log('📊 GET OR CREATE D9 CHART THROUGH BACKEND:', {
      userId: chartData.user_id,
      datetime: chartData.datetime,
      place: chartData.place
    });

    const response = await api.post('/astro/d9chart', chartData);

    logger.log('✅ D9 CHART API RESPONSE:', {
      success: response.data.success,
      source: response.data.source,
      chartId: response.data.data?.d9ChartsId,
      hasGrahaTable: !!response.data.data?.graha_table,
      grahaTableType: typeof response.data.data?.graha_table
    });

    // ✅ CRITICAL: Parse graha_table if it's a string from database
    if (response.data.data && typeof response.data.data.graha_table === 'string') {
      logger.log('🔧 Parsing D9 graha_table from string to array...');
      try {
        response.data.data.graha_table = JSON.parse(response.data.data.graha_table);
        logger.log('✅ D9 graha_table parsed:', response.data.data.graha_table.length, 'planets');
      } catch (parseError) {
        logger.error(' Failed to parse D9 graha_table:', parseError);
      }
    }

    logger.log('✅ D9 CHART LOADED:', {
      success: response.data.success,
      source: response.data.source,
      chartId: response.data.data?.d9ChartsId,
      cached: response.data.cached || false
    });

    return response;
  } catch (error) {
    logger.error(' D9 CHART API ERROR:', error.response?.data || error.message);
    
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error('Backend server is not running! Please start your API server.');
    }

    throw error;
  }
};




/**
 * Get user's D9 charts from database
 * @param {number} userId - User ID
 * @returns {Promise} List of user's D9 charts
 */
export const getUserD9Charts = async (userId) => {
    try {
        logger.log('📊 FETCHING USER D9 CHARTS THROUGH BACKEND', { userId });

        const response = await api.get(`/astro/d9charts/user/${userId}`);

        logger.log('✅ USER D9 CHARTS LOADED', {
            success: response.data.success,
            count: response.data.count
        });

        return response;
    } catch (error) {
        logger.error(' GET USER D9 CHARTS ERROR:', error);

        if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
            throw new Error('Backend server is not running! Please start your API server.');
        }

        throw error;
    }
};

/**
 * Get D9 chart by ID
 * @param {number} chartId - D9 chart ID
 * @returns {Promise} D9 chart details
 */
export const getD9ChartById = async (chartId) => {
    try {
        logger.log('📊 FETCHING D9 CHART BY ID THROUGH BACKEND', { chartId });

        const response = await api.get(`/astro/d9chart/${chartId}`);

        logger.log('✅ D9 CHART BY ID LOADED', {
            success: response.data.success,
            chartId: response.data.chart?.chartId
        });

        return response;
    } catch (error) {
        logger.error(' GET D9 CHART BY ID ERROR:', error);

        if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
            throw new Error('Backend server is not running! Please start your API server.');
        }

        throw error;
    }
};

/**
 * Delete D9 chart
 * @param {number} chartId - D9 chart ID to delete
 * @param {number} userId - User ID (for verification)
 * @returns {Promise} Deletion confirmation
 */
export const deleteD9Chart = async (chartId, userId) => {
    try {
        logger.log('📊 DELETING D9 CHART THROUGH BACKEND', { chartId, userId });

        const response = await api.delete(`/astro/d9chart/${chartId}`, {
            data: { userId }
        });

        logger.log('✅ D9 CHART DELETED', {
            success: response.data.success,
            chartId: response.data.chartId
        });

        return response;
    } catch (error) {
        logger.error(' DELETE D9 CHART ERROR:', error);

        if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
            throw new Error('Backend server is not running! Please start your API server.');
        }

        throw error;
    }
};


export const getAccountActionStatus = async (userId, accountActionId = null) => {
  try {
    logger.log('📊 FETCHING ACCOUNT ACTION STATUS THROUGH BACKEND:', {
      userId,
      accountActionId
    });

    let url = `/astro/account-action/status?userId=${userId}`;
    if (accountActionId) {
      url += `&accountActionId=${accountActionId}`;
    }

    const response = await api.get(url);

    logger.log('✅ ACCOUNT ACTION STATUS LOADED:', {
      success: response.data.success,
      count: response.data.count
    });

    return response.data;
  } catch (error) {
    logger.error('❌ GET ACCOUNT ACTION STATUS ERROR:', error);
    
    // Return proper structure on 404
    if (error.response?.status === 404) {
      return {
        success: false,
        error: 'No account actions found',
        data: null
      };
    }

    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error('❌ Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

export const submitAccountAction = async (actionData) => {
  try {
    // Get user data from localStorage
    const userData = JSON.parse(localStorage.getItem('astroguru_user') || '{}');
    const userId = userData.userId || userData.id;
    const email = userData.email;

    if (!userId || !email) {
      throw new Error('User not authenticated');
    }

    logger.log('📝 SUBMITTING ACCOUNT ACTION THROUGH BACKEND:', {
      userId,
      email,
      action: actionData.action,
      reasonLength: actionData.reason.length
    });

    const response = await api.post('/astro/account-action', {
      userId: userId.toString(),
      email,
      reason: actionData.reason,
      actionType: actionData.action,
      isAdminAction: false,
      createdBy: userId.toString()
    });

    logger.log('✅ ACCOUNT ACTION SUBMITTED:', {
      success: response.data.success,
      accountActionId: response.data.accountAction?.accountActionId
    });

    return response.data;
  } catch (error) {
    logger.error('❌ SUBMIT ACCOUNT ACTION ERROR:', error);
    
    if (error.response?.status === 409) {
      return {
        success: false,
        message: error.response.data.message || 'Action already exists',
        error: error.response.data.error
      };
    }

    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error('❌ Backend server is not running! Please start your API server.');
    }
    
    throw error;
  }
};

export const updateAccountActionStatus = async (actionId, userId, status, modifiedBy = null) => {
  try {
    logger.log('🔄 UPDATING ACCOUNT ACTION STATUS THROUGH BACKEND:', {
      actionId,
      userId,
      status
    });

    const response = await api.put(`/astro/account-action/${actionId}/status`, {
      userId,
      status,
      modifiedBy: modifiedBy || userId
    });

    logger.log('✅ ACCOUNT ACTION STATUS UPDATED:', {
      success: response.data.success,
      newStatus: response.data.accountAction?.newStatus
    });

    return response.data;
  } catch (error) {
    logger.error('❌ UPDATE ACCOUNT ACTION STATUS ERROR:', error);
    
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error('❌ Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

export const getAccountActionsByUser = async (userId, limit = 10, offset = 0) => {
  try {
    logger.log('📋 FETCHING ACCOUNT ACTIONS BY USER THROUGH BACKEND:', {
      userId,
      limit,
      offset
    });

    const response = await api.get(`/astro/account-action/user/${userId}?limit=${limit}&offset=${offset}`);

    logger.log('✅ ACCOUNT ACTIONS BY USER LOADED:', {
      success: response.data.success,
      totalCount: response.data.pagination?.totalCount
    });

    return response.data;
  } catch (error) {
    logger.error('❌ GET ACCOUNT ACTIONS BY USER ERROR:', error);
    
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error('❌ Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

/**
 * Delete account action
 * @param {number} actionId - Account action ID
 * @param {string} userId - User ID
 * @returns {Promise} Deletion result
 */
export const deleteAccountAction = async (actionId, userId) => {
  try {
    logger.log('🗑️ DELETING ACCOUNT ACTION THROUGH BACKEND:', {
      actionId,
      userId
    });

    const response = await api.delete(`/astro/account-action/${actionId}`, {
      data: { userId }
    });

    logger.log('✅ ACCOUNT ACTION DELETED:', {
      success: response.data.success,
      accountActionId: response.data.deletedAction?.accountActionId
    });

    return response.data;
  } catch (error) {
    logger.error('❌ DELETE ACCOUNT ACTION ERROR:', error);
    
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error('❌ Backend server is not running! Please start your API server.');
    }
    throw error;
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// ✅ DIVISIONAL CHARTS API (D1-D10)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get or create any divisional chart (D1-D10)
 * @param {string} chartType - Chart type (d1chart, d2chart, d3chart, ... d10chart)
 * @param {Object} chartData - Chart generation data
 * @returns {Promise} Divisional chart data with parsed graha_table
 */
export const getDivisionalChart = async (chartType, chartData) => {
  try {
    const endpoint = `/astro/${chartType}`;
    
    logger.log(`📊 FETCHING ${chartType.toUpperCase()} THROUGH BACKEND:`, {
      endpoint,
      userId: chartData.user_id,
      datetime: chartData.datetime,
      place: chartData.place,
      timezone: chartData.timezone
    });

    const response = await api.post(endpoint, chartData, { timeout: 30000 });

    if (!response.data?.success) {
      throw new Error(`Backend returned success: false - ${response.data?.message || 'Unknown error'}`);
    }

    logger.log(`✅ ${chartType.toUpperCase()} API RESPONSE:`, {
      success: response.data.success,
      source: response.data.source,
      hasData: !!response.data.data,
      hasGrahaTable: !!response.data.data?.graha_table,
      grahaTableType: typeof response.data.data?.graha_table,
      chartType: response.data.data?.chart_type
    });

    // ✅ CRITICAL: Ensure graha_table is an array
    if (response.data.data) {
      let grahaTable = response.data.data.graha_table;
      
      // Case 1: graha_table is a JSON string
      if (typeof grahaTable === 'string') {
        logger.log(`🔧 Parsing ${chartType} graha_table from JSON string...`);
        try {
          grahaTable = JSON.parse(grahaTable);
        } catch (parseError) {
          logger.error(`❌ Failed to parse ${chartType} graha_table string:`, parseError.message);
          grahaTable = [];
        }
      }
      
      // Case 2: graha_table is an object (convert to array)
      if (typeof grahaTable === 'object' && !Array.isArray(grahaTable)) {
        logger.warn(`⚠️ ${chartType} graha_table is an object, converting to array...`);
        if (grahaTable && Object.keys(grahaTable).length > 0) {
          // Convert object to array of values
          grahaTable = Object.values(grahaTable);
          logger.log(`✅ Converted object to array with ${grahaTable.length} entries`);
          
          // 🔍 DEBUG: Show first graha object structure
          if (grahaTable.length > 0) {
            logger.log(`🔍 ${chartType} First graha object keys:`, Object.keys(grahaTable[0] || {}));
            logger.log(`🔍 ${chartType} First graha object:`, grahaTable[0]);
          }
        } else {
          grahaTable = [];
        }
      }
      
      // Case 3: Ensure it's always an array
      if (!Array.isArray(grahaTable)) {
        logger.warn(`⚠️ ${chartType} graha_table is neither string nor object, defaulting to empty array`);
        grahaTable = [];
      }
      
      response.data.data.graha_table = grahaTable;
      logger.log(`✅ ${chartType} graha_table finalized:`, grahaTable.length, 'planets');
      
      // 🔍 DEBUG: Log full graha_table for inspection
      if (grahaTable.length > 0) {
        logger.log(`🔍 ${chartType} Full parsed graha_table:`, grahaTable);
      }
    }

    logger.log(`✅ ${chartType} READY FOR DISPLAY:`, {
      success: true,
      source: response.data.source,
      grahaCount: response.data.data?.graha_table?.length || 0,
      ayanamsa: response.data.data?.ayanamsa,
      chartType: response.data.data?.chart_type
    });

    return response;
  } catch (error) {
    logger.error(`❌ ${chartType.toUpperCase()} API ERROR:`, {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseData: error.response?.data
    });
    
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error('Backend server is not running! Please start your API server.');
    }

    if (error.code === 'ECONNABORTED') {
      throw new Error(`${chartType} calculation timeout - backend taking too long`);
    }

    throw error;
  }
};

/**
 * Get D1 (Rashi) Chart
 */
export const getD1ChartData = async (chartData) => getDivisionalChart('d1chart', chartData);

/**
 * Get D2 (Hora) Chart
 */
export const getD2ChartData = async (chartData) => getDivisionalChart('d2chart', chartData);

/**
 * Get D3 (Drekkana) Chart
 */
export const getD3ChartData = async (chartData) => getDivisionalChart('d3chart', chartData);

/**
 * Get D4 (Chaturthamsha) Chart
 */
export const getD4ChartData = async (chartData) => getDivisionalChart('d4chart', chartData);

/**
 * Get D5 (Panchamsha) Chart
 */
export const getD5ChartData = async (chartData) => getDivisionalChart('d5chart', chartData);

/**
 * Get D6 (Shashthamsha) Chart
 */
export const getD6ChartData = async (chartData) => getDivisionalChart('d6chart', chartData);

/**
 * Get D7 (Saptamsha) Chart
 */
export const getD7ChartData = async (chartData) => getDivisionalChart('d7chart', chartData);

/**
 * Get D8 (Ashtamsha) Chart
 */
export const getD8ChartData = async (chartData) => getDivisionalChart('d8chart', chartData);

/**
 * Get D9 (Navamsha) Chart - Marriage & Relationships
 */
export const getD9ChartData = async (chartData) => getDivisionalChart('d9chart', chartData);

/**
 * Get D10 (Dasamsha) Chart - Career & Profession
 */
export const getD10ChartData = async (chartData) => getDivisionalChart('d10chart', chartData);

/**
 * Get D11 (Rudramsha) Chart - Income & Gains
 */
export const getD11ChartData = async (chartData) => getDivisionalChart('d11chart', chartData);

/**
 * Get D12 (Dwadashamsha) Chart - Parents & Ancestral Wealth
 */
export const getD12ChartData = async (chartData) => getDivisionalChart('d12chart', chartData);

/**
 * Get D16 (Shodashamsha) Chart - Vehicles & Accidents
 */
export const getD16ChartData = async (chartData) => getDivisionalChart('d16chart', chartData);

/**
 * Get D20 (Vimshamsha) Chart - Spiritual Practices
 */
export const getD20ChartData = async (chartData) => getDivisionalChart('d20chart', chartData);

/**
 * Get D24 (Chaturvimshamsha) Chart - Education & Intellect
 */
export const getD24ChartData = async (chartData) => getDivisionalChart('d24chart', chartData);

/**
 * Get D27 (Saptavimshamsha) Chart - Health & Stamina
 */
export const getD27ChartData = async (chartData) => getDivisionalChart('d27chart', chartData);

/**
 * Get D30 (Trimshamsha) Chart - Misfortune & Calamities
 */
export const getD30ChartData = async (chartData) => getDivisionalChart('d30chart', chartData);

/**
 * Get D40 (Chatvarimshamsa) Chart - General Results
 */
export const getD40ChartData = async (chartData) => getDivisionalChart('d40chart', chartData);

/**
 * Get D45 (Akshavedamsha) Chart - Combined Effects
 */
export const getD45ChartData = async (chartData) => getDivisionalChart('d45chart', chartData);

/**
 * Get D60 (Shashtiamsha) Chart - Ultimate Divisional Chart
 */
export const getD60ChartData = async (chartData) => getDivisionalChart('d60chart', chartData);

/**
 * Get Mahadasha / Dasha timeline and details
 * Expects same chartData payload as divisional charts
 */
export const getMahadasha = async (chartData) => {
  try {
    const endpoint = `/astro/mahadasha`;
    logger.log(`📊 FETCHING MAHADASHA THROUGH BACKEND:`, { endpoint, userId: chartData.user_id, datetime: chartData.datetime });

    const response = await api.post(endpoint, chartData, { timeout: 30000 });

    if (!response.data?.success) {
      throw new Error(`Backend returned success: false - ${response.data?.message || 'Unknown error'}`);
    }

    logger.log(`✅ MAHADASHA API RESPONSE:`, { success: response.data.success, source: response.data.source, hasData: !!response.data.data });
    return response;
  } catch (error) {
    logger.error('❌ MAHADASHA API ERROR:', { message: error.message, responseData: error.response?.data });
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error('Backend server is not running! Please start your API server.');
    }
    if (error.code === 'ECONNABORTED') {
      throw new Error('Mahadasha calculation timeout - backend taking too long');
    }
    throw error;
  }
};

export const getDashaPeriods = async (dashaData) => {
  try {
    logger.log(`📊 FETCHING DASHA PERIODS FROM CHART API:`, { name: dashaData.name, start_date: dashaData.start_date, end_date: dashaData.end_date });

    // ✅ Use proxy for CORS in development
    const response = await axios.post(
      '/api/v1/dasha',
      dashaData,
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        timeout: 30000 // 30 seconds timeout
      }
    );

    logger.log(`✅ DASHA API RESPONSE:`, { success: response.data.status === 'success', hasData: !!response.data });
    return response;
  } catch (error) {
    logger.error('❌ DASHA API ERROR:', { message: error.message, responseData: error.response?.data });
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      throw new Error('Chart API server is not accessible! Please check network connection.');
    }
    if (error.code === 'ECONNABORTED') {
      throw new Error('Dasha calculation timeout - chart API taking too long');
    }
    throw error;
  }
};;




export { api };





export default {
  searchPlaces,
  getPlaceDetails,
  sendMessage,
  googleSignup,
  googleLogin,
  getUserSettings,        // ✅ NEW
  updateUserSettings,
  getUserProfile,
  getUserCredits,
  deductCredits,
  addCredits,
  getCreditHistory,
  saveChatMessage,      // ✅ NEW
  getQuickResponse,     // ✅ NEW
  getChatHistory,       // ✅ NEW
  deleteChatHistory,
  getAllPrompts,
  getPrompt,
  getTypingMessages,
  getReligionData,
  getPromptsVersion,
  reloadPrompts,
  healthCheck,
  getAllGurus,
  getAllConsultationTypes,
  getGuruAvailability,
  getWeekAvailability,
  createBooking,
  getUserBookings,
  getBookingDetails,
  cancelBooking,
  createSession,
  endSession,
  getUserSessions,
  getActiveSession,
  getUserReferralCode,
  generateUserReferralCode,
  verifyReferralCode,
  getAllCountries,           // ✅ NEW
  getPopularCountries,       // ✅ NEW
  getCountriesByRegion,      // ✅ NEW
  getAllRegions,             // ✅ NEW
  searchCountries,
  getReferralMilestones,      // ✅ NEW
  getUserReferralProgress,    // ✅ NEW
  getUserReferralHistory,     // ✅ NEW
  getUserMilestoneRewards,    // ✅ NEW
  getReferralLeaderboard,     // ✅ NEW
  getCreditPackageSummary,
  getCreditPackage,
  getCreditPackages,
  purchaseCredits,
  getSpendTypes,
  spendCredits,
  sendEmailNotification,
  submitFeedback,
  getAllFeedback,
  getUserFeedback,
  updateFeedbackStatus,
  deleteFeedback,
  getReportTypes,
  generateReport,
  getReportHistory,
  getLoggerStatus,
  getDivisionalChart,
  getD1ChartData,
  getD2ChartData,
  getD3ChartData,
  getD4ChartData,
  getD5ChartData,
  getD6ChartData,
  getD7ChartData,
  getD8ChartData,
  getD9ChartData,
  getD10ChartData,
  getD11ChartData,
  getD12ChartData,
  getD16ChartData,
  getD20ChartData,
  getD24ChartData,
  getD27ChartData,
  getD30ChartData,
  getD40ChartData,
  getD45ChartData,
  getD60ChartData,
  getMahadasha,
  getDashaPeriods,
  generateD1Chart,
  getD1ChartHealth,
  getOrCreateD1Chart,
  getUserD1Charts,
  getD1ChartById,
  deleteD1Chart,
  getOrCreateD9Chart,
  getUserD9Charts,
  getD9ChartById,
  deleteD9Chart,
  getAccountActionStatus,
  submitAccountAction,
  updateAccountActionStatus,
  getAccountActionsByUser,
  deleteAccountAction,
  verifyAndCompletePayment,
  getPaymentDetails,
  api
};
